import { useEffect } from "react";
import { type TradeInAemDispatchType, useTradeInAemSelector } from "../../redux/store";
import { getInternalLink } from "../../utils/navUtils";
import { useTradeInCsl } from "../../csl/tradeInCslUtils";
import { TradeInAemVersion } from "../../utils/constants";
import { useTradeInAemLabel } from "../../utils/labels";
import {
    setTradeInAemExtData,
    setTradeInAemShowModal,
} from "../../../common-deprecated/features/tradein/redux/actions/TradeInAemExternalDataActions";
import { isDevMode } from "../../utils/utils";
import Debug from "../../../common-deprecated/Debug";
import { clearModal } from "../../../common-deprecated/redux/actions/ModalActions";
import { MODAL_INTEGRATED_TRADE_IN_AEM } from "../../../shared-logic/features/tradein/utils/integratedModalConstants";
import { useDispatch } from "../../../shared-logic/redux/store";

type ReturnType = {
    mainCtaProps: {
        onClick?: (event?: React.SyntheticEvent) => void;
        href?: string;
        label: string;
    };
};

const useVehicleValueContainer = (): ReturnType => {
    const dispatch = useDispatch<TradeInAemDispatchType>();
    const csl = useTradeInCsl();
    const valuation = useTradeInAemSelector((state) => state.tradeInAemVehicleValue.valuation);
    const currentStep = useTradeInAemSelector((state) => state.tradeInAemNav.step);
    const commonSettings = useTradeInAemSelector((state) => state.commonSettings);
    const version = useTradeInAemSelector((state) => state.tradeInAemUtils.version);
    const nextStep = getInternalLink(csl, currentStep, dispatch, commonSettings, "next");
    const [continueLabel, addToDealLabel] = useTradeInAemLabel([
        "tradeInAemVehicleValueGetQuote",
        "tradeInAemVehicleValueAddToBasket",
    ]);

    // Dev mode helper.
    useEffect(() => {
        if (!valuation) {
            // Initiate value fetching in dev mode as this is useful for layout or other debugging purposes (This would require hardcoding the fetch result for it to work properly).
            // If this is triggered outside of dev mode it is a bug as the onStepTransition method should handle this.
            // The loading state is handled on a higher level (see TradeInAem containers) so the vehicle value should have already been fetched before this component is rendered.
            if (isDevMode(commonSettings)) {
                csl.fetchVehicleQuote();
                Debug.warn(
                    "VehicleValueContainer rendered without a quote present, loading valuation manually (devMode only)",
                );
            } else {
                Debug.error(
                    "VehicleValueContainer rendered without a quote present, this is a bug and should be fixed",
                );
            }
        }
    }, []);

    // Click handler for integrated trade-in, for now we assume integrated trade-in always ends on the value step.
    const integratedTradeInClick = (): void => {
        // In theory we can read trade-in data straight from the store and propagate to the external data reducer.
        // However, for consistency we use the valuation response as this is also the way external data is populated in other cases.
        const externalPriceData = csl.getExtDataFromValuation(valuation);

        // The if statement is here for TS purposes but in practice we should always have data if CSL is implemented properly.
        if (externalPriceData) dispatch(setTradeInAemExtData(externalPriceData));
        // Closes the integrated modal for new cars integration
        dispatch(clearModal(MODAL_INTEGRATED_TRADE_IN_AEM)); // Using deprecated clearModal() as the modal needs to be destroyed from the DOM. Else this would interfere with the finance modal.
        // Closes the integrated modal for apheleia integration
        dispatch(setTradeInAemShowModal(false));
    };

    const mainCtaProps: ReturnType["mainCtaProps"] =
        version === TradeInAemVersion.Standalone
            ? { label: continueLabel, ...nextStep }
            : { label: addToDealLabel, onClick: integratedTradeInClick };

    return { mainCtaProps };
};

export default useVehicleValueContainer;
